import { axiosInstance } from '@/services/api/axios';
import type {
  CandidateChecklistFileUrl,
  CandidateChecklistItemUpdate,
  CandidateChecklistPortal,
} from '@/services/api/types';

/**
 * Activation checklist endpoints for the signed-in candidate
 * (`/candidate/checklist-portal/:candidateId/*` in ems-api).
 *
 * `candidateId` is the hiring `candidates.id`, which is the `id` of a
 * `hiring_candidate` row from GET /candidates/me/applications. The API checks
 * that the row's email matches the signed-in account and answers 404 otherwise.
 * All calls go through `axiosInstance` for the candidate bearer token,
 * X-Device-Signature and the 401 refresh.
 */
const base = (candidateId: string) =>
  `candidate/checklist-portal/${encodeURIComponent(candidateId)}`;

const itemPath = (candidateId: string, itemId: string) =>
  `${base(candidateId)}/checklist-items/${encodeURIComponent(itemId)}`;

export const candidateChecklistApi = {
  getPortal: async (candidateId: string): Promise<CandidateChecklistPortal> => {
    const res = await axiosInstance.get(base(candidateId));
    return res.data.data;
  },

  /** Saves a non-file answer. The API refuses approved items and closed checklists. */
  saveValue: async (
    candidateId: string,
    itemId: string,
    value: unknown,
  ): Promise<CandidateChecklistItemUpdate> => {
    const res = await axiosInstance.patch(itemPath(candidateId, itemId), { value });
    return res.data.data;
  },

  /** Uploads a file for a file_upload item; the API stores it and saves its key on the item. */
  uploadFile: async (
    candidateId: string,
    itemId: string,
    file: File,
  ): Promise<CandidateChecklistItemUpdate> => {
    const form = new FormData();
    form.append('file', file);
    // Unset the instance's JSON default so the browser writes the multipart
    // boundary itself (same as resumeApi.parseResume).
    const res = await axiosInstance.post(`${itemPath(candidateId, itemId)}/upload`, form, {
      headers: { 'Content-Type': undefined as unknown as string },
    });
    return res.data.data;
  },

  /** Short-lived signed URL for a file already uploaded to an item. */
  getFileUrl: async (candidateId: string, itemId: string): Promise<CandidateChecklistFileUrl> => {
    const res = await axiosInstance.get(`${itemPath(candidateId, itemId)}/view-url`);
    return res.data.data;
  },
};
